import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faPlus,
  faChevronDown,
  faChevronUp,
  faTimes,
  faUpload,
  faCheck,
  faSearch,
  faLock,
  faClock,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { getEnabledPlatforms } from '../utils/accountSettingPlatformList';
import { useAccount } from '../contexts/AccountContext';
import { useEnabledChannels } from '../hooks/useEnabledChannels';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useChannelConfigs } from '../hooks/useChannelConfigs';
import { getUserData } from '../utils/userUtils';
import { hasChannelAccess, isSuperAdmin } from '../utils/permissions';
import './CampaignCreation.css'; // Reuse styles for folder/destination selection
import './PacingCreation.css';

/**
 * PacingCreation component for creating new pacing schedules
 * Implements the hierarchy: Account → Folder → Pacing
 *
 * Features:
 * 1. Pacing type selection (Manual/Auto)
 * 2. Folder selection or creation
 * 3. Pacing name input
 * 4. Destination/channel selection based on pacing-enabled platforms
 * 5. Excel file upload for pacing data
 */
// Excel validation rows can carry a non-numeric daily_budget (e.g. a stray
// header row parsed as data) — render the raw value instead of crashing on
// toFixed, which blanks the whole page.
const formatBudgetCell = (value) => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};

const PacingCreation = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { enabledChannels, isLoading: channelsLoading } = useEnabledChannels(accountId, user);
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const { getChannelsForPlatform, hasMultipleChannels, isLoading: channelConfigsLoading } = useChannelConfigs(accountId, user);

  // Get pacing-enabled platforms (comma-separated list, e.g. "facebook,ttd").
  // Canonicalized to destination ids so any accepted alias works ('g-ads-search'
  // resolves to 'google-ads-search' before the .includes(dest.id) checks below).
  const pacingEnabledPlatforms = getEnabledPlatforms(accountSettings?.['pacing-enabled']);

  // Get bulk-auto-pacing enabled platforms (comma-separated list, e.g. "google-ads-search,meta")
  const bulkAutoPacingEnabledPlatforms = getEnabledPlatforms(accountSettings?.['bulk-auto-pacing']);

  // Component state
  const [pacingType, setPacingType] = useState('manual'); // 'manual' or 'auto'
  const [pacingName, setPacingName] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(null); // Selected channel config ID (for platforms with multiple configs)
  // Note: ad_account_id for Facebook is now read from Excel file per row
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null); // For detailed Excel validation errors

  // Auto pacing specific state
  const [flightStartDate, setFlightStartDate] = useState('');
  const [flightEndDate, setFlightEndDate] = useState('');
  const [executionTime, setExecutionTime] = useState('09:00');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [minDailyBudget, setMinDailyBudget] = useState('1.00');

  // Get today's date in YYYY-MM-DD format for min date validation (using local time, not UTC)
  const today = (() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Folder search and pagination state
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderPage, setFolderPage] = useState(1);
  const [folderPagination, setFolderPagination] = useState(null);
  const [isLoadingMoreFolders, setIsLoadingMoreFolders] = useState(false);
  const folderDropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Check if user has access to a channel
  const userHasChannelAccess = (channelId) => {
    if (isSuperAdmin(user)) return true;
    return hasChannelAccess(user, channelId);
  };

  // All possible destinations
  const allDestinations = [
    { id: 'dv360', name: 'DV360', icon: 'smart_display', enabled: enabledChannels?.dv360 },
    { id: 'ttd', name: 'The Trade Desk', icon: 'ads_click', enabled: enabledChannels?.ttd },
    { id: 'youtube-dv360', name: 'YouTube DV360', icon: 'play_circle', enabled: enabledChannels?.['youtube-dv360'] },
    { id: 'facebook', name: 'Meta', icon: 'facebook', enabled: enabledChannels?.facebook },
    { id: 'google-ads-search', name: 'Google Search', icon: 'search', enabled: enabledChannels?.['google-ads-search'] },
    { id: 'google-ads-pmax', name: 'Google PMax', icon: 'ads_click', enabled: enabledChannels?.['google-ads-pmax'] },
    { id: 'google-ads-demandgen', name: 'Google DemandGen', icon: 'auto_awesome', enabled: enabledChannels?.['google-ads-demandgen'] },
    { id: 'tiktok', name: 'TikTok', icon: 'music_note', enabled: enabledChannels?.tiktok },
    { id: 'madhive', name: 'MadHive', icon: 'tv', enabled: enabledChannels?.madhive },
    { id: 'amazon-dsp', name: 'Amazon DSP', icon: 'shopping_cart', enabled: enabledChannels?.['amazon-dsp'] },
    { id: 'gam', name: 'Google Ad Manager', icon: 'ad_units', enabled: enabledChannels?.['google-ad-manager'] },
    { id: 'groundtruth', name: 'GroundTruth', icon: 'location_on', enabled: enabledChannels?.groundtruth },
    { id: 'reddit', name: 'Reddit', icon: 'forum', enabled: enabledChannels?.reddit },
    { id: 'linkedin', name: 'LinkedIn', icon: 'work', enabled: enabledChannels?.linkedin }
  ];

  // Available destinations based on pacing type, enabled platforms, and user permissions
  const availableDestinations = allDestinations.filter(dest => {
    if (!dest.enabled || !userHasChannelAccess(dest.id)) return false;

    if (pacingType === 'auto') {
      return bulkAutoPacingEnabledPlatforms.includes(dest.id);
    } else {
      return pacingEnabledPlatforms.includes(dest.id);
    }
  });

  // Check if auto pacing is available for any platform
  const isAutoPacingAvailable = bulkAutoPacingEnabledPlatforms.length > 0;

  // Fetch folders on component mount
  useEffect(() => {
    if (accountId) {
      fetchFolders();
    }
  }, [accountId]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!showFolderDropdown) {
      setFolderSearchTerm('');
      setFolderPage(1);
      if (accountId) {
        fetchFolders('', 1, false);
      }
    }
  }, [showFolderDropdown, accountId]);

  // Auto-select channel if only one is available
  useEffect(() => {
    if (availableDestinations.length === 1 && !selectedDestination) {
      setSelectedDestination(availableDestinations[0].id);
    }
  }, [availableDestinations, selectedDestination]);

  // Auto-select or reset channel config when destination changes
  useEffect(() => {
    if (selectedDestination) {
      const channels = getChannelsForPlatform(selectedDestination);
      if (channels.length === 1) {
        // Auto-select if only one channel config
        setSelectedChannelId(channels[0].id);
      } else if (channels.length > 1) {
        // Reset selection if multiple configs exist (user must choose)
        setSelectedChannelId(null);
      } else {
        // No channel configs available
        setSelectedChannelId(null);
      }
    } else {
      // No destination selected - reset channel selection
      setSelectedChannelId(null);
    }
  }, [selectedDestination, getChannelsForPlatform]);

  const fetchFolders = useCallback(async (searchTerm = '', page = 1, append = false) => {
    if (!accountId) return;

    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMoreFolders(true);
      }

      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: searchTerm,
          page,
          limit: 50,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      const newFolders = response.data.folders || response.data || [];

      if (append) {
        setFolders(prev => [...prev, ...newFolders]);
      } else {
        setFolders(newFolders);
      }

      setFolderPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching folders:', err);
      if (!append) {
        setError('Failed to load folders');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMoreFolders(false);
    }
  }, [accountId, user]);

  // Debounced search handler
  const handleFolderSearch = useCallback((searchTerm) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setFolderPage(1);
      fetchFolders(searchTerm, 1, false);
    }, 300);
  }, [fetchFolders]);

  // Handle folder dropdown scroll for infinite loading
  const handleFolderDropdownScroll = useCallback((e) => {
    const element = e.target;
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (isNearBottom &&
        folderPagination?.hasNextPage &&
        !isLoadingMoreFolders &&
        !isLoading) {
      const nextPage = folderPage + 1;
      setFolderPage(nextPage);
      fetchFolders(folderSearchTerm, nextPage, true);
    }
  }, [folderPagination, isLoadingMoreFolders, isLoading, folderPage, folderSearchTerm, fetchFolders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !accountId) return;

    try {
      setIsLoading(true);
      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        accountId,
        userId: user?.userId || user?.id
      });

      const newFolder = response.data;
      setFolders(prev => [...prev, newFolder]);
      setSelectedFolder(newFolder);
      setNewFolderName('');
      setShowNewFolderForm(false);
      setShowFolderDropdown(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    // Only accept Excel files
    const excelFiles = files.filter(file =>
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.csv')
    );

    if (excelFiles.length === 0) {
      setError('Please upload an Excel file (.xlsx, .xls) or CSV file');
      return;
    }

    setUploadedFiles(excelFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const excelFiles = files.filter(file =>
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.csv')
    );

    if (excelFiles.length === 0) {
      setError('Please upload an Excel file (.xlsx, .xls) or CSV file');
      return;
    }

    setUploadedFiles(excelFiles);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDestinationSelect = (destId) => {
    setSelectedDestination(destId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Build form data for file upload
      const formData = new FormData();
      formData.append('name', pacingName);
      formData.append('folderId', selectedFolder.id);
      formData.append('pacingType', pacingType);

      // For auto pacing, add additional fields and map platform to backend format
      if (pacingType === 'auto') {
        // Map frontend platform ID to backend platform format
        const platformMapping = {
          'google-ads-search': 'google_ads',
          'google-ads-pmax': 'google_ads',
          'google-ads-demandgen': 'google_ads',
          'facebook': 'meta',
          'tiktok': 'tiktok'
        };
        formData.append('platform', platformMapping[selectedDestination] || selectedDestination);
        formData.append('flightStartDate', flightStartDate);
        formData.append('flightEndDate', flightEndDate);
        formData.append('executionTime', executionTime);
        formData.append('timezone', timezone);
        formData.append('minDailyBudget', minDailyBudget);
      } else {
        formData.append('platform', selectedDestination);
      }

      // For Facebook, ad_account_id is now read from Excel file per row (no form field needed)

      // Add channel ID if selected (for platforms with multiple configs)
      if (selectedChannelId) {
        formData.append('channelId', selectedChannelId);
      }

      // Append the first Excel file
      if (uploadedFiles.length > 0) {
        formData.append('file', uploadedFiles[0]);
      }

      // Determine the endpoint based on pacing type
      const userId = user?.userId || user?.id;
      // getApiUrl resolves same-origin (https://<current host>/backend) in
      // production — never the cross-origin REACT_APP_API_URL baked at build,
      // which pins every subdomain's calls to sidekick.stellair.ai.
      const endpoint = pacingType === 'auto'
        ? getApiUrl(`/api/v2/bulk-auto-pacing?userId=${userId}&accountId=${accountId}`)
        : getApiUrl(`/api/v2/pacing?userId=${userId}&accountId=${accountId}`);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        },
        body: formData
      });

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error(`Server error (${response.status}): ${response.statusText || 'Unknown error'}`);
      }

      if (!response.ok) {
        // Check if there are detailed validation errors
        if (result.validationErrors || result.skippedRows) {
          setValidationErrors({
            message: result.message || result.error,
            validationErrors: result.validationErrors || [],
            skippedRows: result.skippedRows || [],
            summary: result.summary || null
          });
          setError(null); // Clear simple error when we have detailed errors
          return;
        }
        const errorMsg = typeof result.error === 'string'
          ? result.error
          : result.error?.message || result.message || 'Failed to create pacing';
        throw new Error(errorMsg);
      }

      console.log('Pacing created successfully:', result);

      // Navigate to appropriate details page based on pacing type
      if (pacingType === 'auto') {
        // Bulk auto-pacing returns schedule.id inside a schedule object
        const scheduleId = result.schedule?.id || result.scheduleId;
        navigate(`/pacing/bulk/${scheduleId}`);
      } else {
        navigate(`/pacing/${result.pacingScheduleId}`);
      }
    } catch (err) {
      console.error('Error creating pacing:', err);
      setError(err.message || 'Failed to create pacing schedule');
      setValidationErrors(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const baseValid = (
      pacingName.trim() &&
      selectedFolder &&
      selectedDestination &&
      uploadedFiles.length > 0
    );

    // Validate channel config selection when multiple configs exist
    if (selectedDestination && hasMultipleChannels(selectedDestination) && !selectedChannelId) {
      return false;
    }

    // For auto pacing, also validate flight dates and execution time
    if (pacingType === 'auto') {
      return baseValid &&
        flightStartDate &&
        flightEndDate &&
        executionTime &&
        minDailyBudget &&
        flightStartDate >= today && // Start date cannot be earlier than today
        new Date(flightEndDate) >= new Date(flightStartDate);
    }

    // For Facebook, ad_account_id is now read from Excel file (no form field needed)
    return baseValid;
  };

  // Loading state
  if (isLoading && folders.length === 0) {
    return (
      <div className="campaign-creation-container">
        <div className="loading-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-creation-container">
      <div className="campaign-creation-header">
        <h1>Create Pacing</h1>

        {/* Pacing Type Toggle */}
        <div className="pacing-type-toggle">
          <button
            type="button"
            className={`pacing-type-btn ${pacingType === 'manual' ? 'active' : ''}`}
            onClick={() => {
              setPacingType('manual');
              setSelectedDestination(''); // Reset destination when switching types
            }}
          >
            <span>Manual</span>
          </button>
          <button
            type="button"
            className={`pacing-type-btn ${!isAutoPacingAvailable ? 'disabled' : ''} ${pacingType === 'auto' ? 'active' : ''}`}
            disabled={!isAutoPacingAvailable}
            title={isAutoPacingAvailable ? 'Formula-based auto pacing' : 'Coming soon'}
            onClick={() => {
              if (isAutoPacingAvailable) {
                setPacingType('auto');
                setSelectedDestination(''); // Reset destination when switching types
              }
            }}
          >
            {!isAutoPacingAvailable && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
            <FontAwesomeIcon icon={faClock} style={{ marginRight: '4px' }} />
            <span>Auto</span>
            {!isAutoPacingAvailable && <span className="coming-soon-badge">Coming Soon</span>}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="campaign-creation-form">
        {error && (
          <div className="campaign-creation-error">
            <p>{error}</p>
            <button type="button" onClick={() => setError(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Folder Selection - First */}
        <div className="form-group">
          <label id="folder-select-label">Select Folder *</label>
          <p className="form-help-text">
            Default folder for imported campaigns. Can be overridden per-row using the optional "parent_name" column in your Excel file.
          </p>
          <div className="folder-select-container">
            <button
              type="button"
              className={`folder-select-button ${showFolderDropdown ? 'active' : ''}`}
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              aria-labelledby="folder-select-label"
              aria-expanded={showFolderDropdown}
              aria-haspopup="listbox"
            >
              <FontAwesomeIcon icon={faFolder} />
              <span>
                {selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'Select a folder'}
              </span>
              <FontAwesomeIcon
                icon={showFolderDropdown ? faChevronUp : faChevronDown}
                className="dropdown-arrow"
              />
            </button>

            {showFolderDropdown && (
              <div className="folder-dropdown">
                <div className="folder-search-container">
                  <FontAwesomeIcon icon={faSearch} className="folder-search-icon" />
                  <input
                    type="text"
                    className="folder-search-input"
                    placeholder="Search folders..."
                    value={folderSearchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFolderSearchTerm(value);
                      handleFolderSearch(value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div
                  className="folder-list-container"
                  ref={folderDropdownRef}
                  onScroll={handleFolderDropdownScroll}
                >
                  {folders.length === 0 && !isLoading ? (
                    <div className="folder-empty-state">
                      <p>No folders found</p>
                    </div>
                  ) : (
                    folders.map(folder => (
                      <button
                        key={folder.id}
                        type="button"
                        className={`folder-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFolder(folder);
                          setShowFolderDropdown(false);
                          setFolderSearchTerm('');
                        }}
                      >
                        <FontAwesomeIcon icon={faFolder} />
                        <span>{getFolderDisplayLabel(folder)}</span>
                        {selectedFolder?.id === folder.id && (
                          <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        )}
                      </button>
                    ))
                  )}
                  {isLoadingMoreFolders && (
                    <div className="folder-loading-more">
                      <p>Loading more folders...</p>
                    </div>
                  )}
                </div>

                <div className="folder-dropdown-divider" />

                {!showNewFolderForm ? (
                  <button
                    type="button"
                    className="folder-option create-new"
                    onClick={() => setShowNewFolderForm(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create new folder</span>
                  </button>
                ) : (
                  <div className="new-folder-form">
                    <input
                      type="text"
                      placeholder="Enter folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateFolder();
                        }
                      }}
                      autoFocus
                    />
                    <div className="new-folder-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleCreateFolder}
                        disabled={!newFolderName.trim() || isLoading}
                      >
                        Create
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setShowNewFolderForm(false);
                          setNewFolderName('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Platform Selection */}
        <div className="form-group">
          <label id="destination-select-label">Select Platform *</label>
          <p className="form-help-text">
            Select the platform where your campaigns exist.
          </p>
          <div
            className="destination-grid"
            role="radiogroup"
            aria-labelledby="destination-select-label"
          >
            {channelsLoading ? (
              <div className="loading-destinations">
                <p>Loading available platforms...</p>
              </div>
            ) : availableDestinations.length === 0 ? (
              <div className="no-destinations">
                <p>No platforms available for pacing.</p>
                <p>Please contact your administrator to configure pacing-enabled platforms.</p>
              </div>
            ) : (
              availableDestinations.map(destination => {
                const isSelected = selectedDestination === destination.id;
                return (
                  <button
                    key={destination.id}
                    type="button"
                    className={`destination-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleDestinationSelect(destination.id)}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <span className="material-symbols-outlined destination-icon">
                      {destination.icon}
                    </span>
                    <span className="destination-name">{destination.name}</span>
                    {isSelected && (
                      <FontAwesomeIcon icon={faCheck} className="check-icon" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Channel Configuration - only show when platform has multiple channel configs */}
        {selectedDestination && hasMultipleChannels(selectedDestination) && (
          <div className="form-group">
            <label htmlFor="pacing-channel-config">Channel Configuration *</label>
            {channelConfigsLoading ? (
              <p className="form-help-text">Loading channel configurations...</p>
            ) : (
              <>
                <select
                  id="pacing-channel-config"
                  className="form-control"
                  value={selectedChannelId || ''}
                  onChange={(e) => setSelectedChannelId(e.target.value)}
                  required
                >
                  <option value="">Select a channel configuration</option>
                  {getChannelsForPlatform(selectedDestination).map(channel => {
                    const showAdAccountId = channel.field_values?.ad_account_id;
                    const displayText = showAdAccountId
                      ? `${channel.name} (${channel.field_values.ad_account_id})`
                      : channel.name;
                    return (
                      <option key={channel.id} value={channel.id}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
                <p className="form-help-text">
                  Multiple {availableDestinations.find(d => d.id === selectedDestination)?.name || selectedDestination} configurations are available. Select which one to use for this pacing schedule.
                </p>
              </>
            )}
          </div>
        )}

        {/* Ad Account ID removed for Facebook - now read from Excel file per row */}

        {/* Auto Pacing Configuration - only show when auto pacing is selected */}
        {pacingType === 'auto' && selectedDestination && (
          <div className="auto-pacing-config">
            <h3>
              <FontAwesomeIcon icon={faClock} style={{ marginRight: '8px' }} />
              Auto Pacing Configuration
            </h3>
            <p className="form-help-text">
              Configure the flight period and execution settings. Budgets will be automatically calculated daily using the formula: (Total Budget - Spent) / Days Remaining
            </p>

            {/* Flight Dates */}
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="flight-start-date">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '4px' }} />
                  Flight Start Date *
                </label>
                <input
                  type="date"
                  id="flight-start-date"
                  className="form-control"
                  value={flightStartDate}
                  onChange={(e) => setFlightStartDate(e.target.value)}
                  min={today}
                  required
                />
                <small className="form-text text-muted">Start date cannot be earlier than today</small>
              </div>
              <div className="form-group half">
                <label htmlFor="flight-end-date">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '4px' }} />
                  Flight End Date *
                </label>
                <input
                  type="date"
                  id="flight-end-date"
                  className="form-control"
                  value={flightEndDate}
                  onChange={(e) => setFlightEndDate(e.target.value)}
                  min={flightStartDate || today}
                  required
                />
              </div>
            </div>

            {/* Execution Time and Timezone */}
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="execution-time">
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: '4px' }} />
                  Daily Execution Time *
                </label>
                <input
                  type="time"
                  id="execution-time"
                  className="form-control"
                  value={executionTime}
                  onChange={(e) => setExecutionTime(e.target.value)}
                  required
                />
              </div>
              <div className="form-group half">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  className="form-control"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="UTC">UTC</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Paris (CET/CEST)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                </select>
              </div>
            </div>

            {/* Min Daily Budget */}
            <div className="form-group">
              <label htmlFor="min-daily-budget">Minimum Daily Budget ($) *</label>
              <input
                type="number"
                id="min-daily-budget"
                className="form-control"
                value={minDailyBudget}
                onChange={(e) => setMinDailyBudget(e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="1.00"
                required
              />
              <small className="form-text text-muted">
                The minimum budget to apply per campaign (Google Ads minimum is $1/day)
              </small>
            </div>
          </div>
        )}

        {/* Pacing Name */}
        <div className="form-group">
          <label htmlFor="pacing-name">Pacing Name *</label>
          <input
            type="text"
            id="pacing-name"
            className="form-control"
            placeholder="Enter pacing name"
            value={pacingName}
            onChange={(e) => setPacingName(e.target.value)}
            required
          />
        </div>

        {/* Excel File Upload */}
        <div className="form-group">
          <label>Upload Pacing File *</label>
          <p className="form-help-text">
            Upload an Excel file with the following columns{selectedDestination ? ':' : ' (select a channel first)'}
          </p>
          {selectedDestination && pacingType === 'auto' && (
            <div className="column-requirements">
              <div className="column-group">
                <strong>Required:</strong>
                {(selectedDestination === 'google-ads-search' || selectedDestination === 'google-ads-pmax' || selectedDestination === 'google-ads-demandgen') && (
                  <>
                    <span className="column-tag required">google_campaign_id</span>
                    <span className="column-tag required">adwords_advertiser_id</span>
                  </>
                )}
                {selectedDestination === 'facebook' && (
                  <>
                    <span className="column-tag required">campaign_id</span>
                    <span className="column-tag required">advertiser_id</span>
                  </>
                )}
                <span className="column-tag required">total_media_budget</span>
                <span className="column-tag required">delivered_to_date</span>
                <span className="column-tag required">campaign_name</span>
                <span className="column-tag required">advertiser_name</span>
                <span className="column-tag required">flight_start_date <small>(YYYY-MM-DD)</small></span>
                <span className="column-tag required">flight_end_date <small>(YYYY-MM-DD)</small></span>
                <span className="column-tag required">new_daily_budget</span>
              </div>
              <div className="column-group">
                <strong>Optional:</strong>
                <span className="column-tag optional">parent_name <small>(assign to folder)</small></span>
              </div>
            </div>
          )}
          {selectedDestination && pacingType === 'manual' && (
            <div className="column-requirements">
              <div className="column-group">
                <strong>Required:</strong>
                <span className="column-tag required">campaign_id</span>
                <span className="column-tag required">daily_budget</span>
                {selectedDestination === 'facebook' && (
                  <span className="column-tag required">advertiser_id</span>
                )}
                {selectedDestination === 'google-ads-search' && (
                  <span className="column-tag required">adwords_advertiser_id</span>
                )}
              </div>
              <div className="column-group">
                <strong>Filtering:</strong>
                {(selectedDestination === 'facebook' || selectedDestination === 'google-ads-search') && (
                  <span className="column-tag optional">daily_budget_oti <small>(skip if 80%-120%)</small></span>
                )}
                {selectedDestination === 'facebook' && (
                  <span className="column-tag optional">campaign_pause_date <small>(skip if set)</small></span>
                )}
                {selectedDestination === 'google-ads-search' && (
                  <>
                    <span className="column-tag optional">campaign_status <small>(skip if PAUSED)</small></span>
                    <span className="column-tag optional">flight_status <small>(skip if not LIVE)</small></span>
                  </>
                )}
              </div>
              <div className="column-group">
                <strong>Optional:</strong>
                <span className="column-tag optional">parent_name <small>(assign to folder)</small></span>
              </div>
            </div>
          )}
          <div
            className={`file-upload-area ${isDragOver ? 'drag-over' : ''} ${uploadedFiles.length > 0 ? 'has-files' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              className="file-input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
            />
            <div className="file-upload-content">
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
              <p>Drag & drop your Excel file here, or click to browse</p>
              <p className="file-formats">Supported formats: .xlsx, .xls, .csv</p>
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="uploaded-files-list">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="uploaded-file-item">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => handleRemoveFile(index)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Validation Errors Section - only for required field errors (not filtered/skipped rows) */}
        {validationErrors && validationErrors.validationErrors && validationErrors.validationErrors.length > 0 && (
          <div className="validation-errors-section">
            <div className="validation-errors-header">
              <h3>⚠️ {validationErrors.message}</h3>
              <button type="button" onClick={() => setValidationErrors(null)} className="close-btn">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {validationErrors.summary && (
              <p className="validation-summary">
                {validationErrors.summary.totalRows} total rows in file •
                {validationErrors.summary.validationErrorCount} validation error(s) must be fixed
              </p>
            )}

            {/* Validation Errors Table */}
            <div className="validation-errors-table-container">
              <table className="validation-errors-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Campaign ID</th>
                    <th>Daily Budget</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.validationErrors.map((err, idx) => (
                    <tr key={idx}>
                      <td>{err.rowNumber}</td>
                      <td className="mono">{err.campaignId}</td>
                      <td>{formatBudgetCell(err.dailyBudget)}</td>
                      <td className="error-message">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Rows Filtered Section - when all rows were skipped due to filtering */}
        {validationErrors && validationErrors.summary && validationErrors.summary.uniqueCampaigns === 0 &&
         validationErrors.skippedRows && validationErrors.skippedRows.length > 0 && (
          <div className="validation-errors-section">
            <div className="validation-errors-header">
              <h3>⚠️ {validationErrors.message}</h3>
              <button type="button" onClick={() => setValidationErrors(null)} className="close-btn">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <p className="validation-summary">
              {validationErrors.summary.totalRows} total rows in file •
              {validationErrors.summary.skippedCount} rows filtered out (no campaigns require pacing adjustment)
            </p>

            {/* Skipped Rows Table */}
            <div className="skipped-rows-table-container">
              <h4>Filtered Rows</h4>
              <table className="validation-errors-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Campaign ID</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.skippedRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.rowNumber}</td>
                      <td className="mono">{row.campaignId}</td>
                      <td>{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/pacing')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isFormValid() || isLoading || isUploadingFiles || isSubmitting}
          >
            {isSubmitting ? 'Creating Pacing...' : 'Create Pacing'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PacingCreation;

